import { useMemo } from 'react';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	useServiceCore,
	type AnyService,
	type DependencyOverridesOf,
	type ToResolvedService,
	type ServiceResolverProviding,
} from '@atlassian/expansion-services/core';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { cfetchJSON } from './services/fetch.tsx';
import type { FetchInput, FetchResponse } from '@atlassian/expansion-services/services';

export const useServiceEnvironmentDeps = (): {
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	fetchJSON: <Response>(input: FetchInput) => Promise<FetchResponse<Response>>;
	getCurrentCloudId: () => string;
	getCurrentProduct: () => 'confluence';
	getCurrentUserId: () => string | null;
} => {
	const { cloudId, userId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useMemo(
		() => ({
			createAnalyticsEvent,
			fetchJSON: cfetchJSON,
			getCurrentCloudId: () => cloudId,
			getCurrentProduct: () => 'confluence',
			getCurrentUserId: () => userId,
		}),
		[createAnalyticsEvent, cloudId, userId],
	);
};

type EnvironmentDeps = ReturnType<typeof useServiceEnvironmentDeps>;

export const useService: ServiceResolverProviding<EnvironmentDeps> = <
	DeclaredService extends AnyService,
>(
	service: DeclaredService,
	providedDeps?: DependencyOverridesOf<DeclaredService, EnvironmentDeps>,
): ToResolvedService<DeclaredService> => {
	const environmentDeps = useServiceEnvironmentDeps();
	const deps = useMemo(
		() => ({ ...environmentDeps, ...providedDeps }),
		[environmentDeps, providedDeps],
	);

	return useServiceCore(service, deps as never);
};
